import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, HeartIcon, Loader2Icon } from 'lucide-react';
import { donationAmounts } from '../data/donations';
type Frequency = 'once' | 'monthly';
type Status = 'idle' | 'loading' | 'success';
const EASE = [0.23, 1, 0.32, 1] as const;
export function DonationPicker() {
  const [frequency, setFrequency] = useState<Frequency>('monthly');
  const [selected, setSelected] = useState<number | 'custom'>(50);
  const [custom, setCustom] = useState('');
  const [status, setStatus] = useState<Status>('idle');
  const amount = selected === 'custom' ? Number(custom) || 0 : selected;
  const impact = selected === 'custom' ? 'Every dollar keeps Honduran culture thriving and opens doors for our community.' : donationAmounts.find(d => d.amount === selected)?.impact;
  const handleDonate = () => {
    if (amount <= 0) return;
    setStatus('loading');
    window.setTimeout(() => setStatus('success'), 900);
  };
  return <div className="flex h-full flex-col rounded-[2rem] bg-ink p-8 text-sand md:p-10" data-id="element-24" data-mp="2xj60255xgf8v">
      <div className="flex flex-wrap items-center justify-between gap-4" data-id="element-25" data-mp="32gfxo83gzx7s">
        <h3 className="font-display text-3xl uppercase md:text-4xl" data-id="element-26" data-mp="2oihln9cmgcvg">Give with pride</h3>
        <div className="relative flex rounded-full bg-sand/10 p-1" role="radiogroup" aria-label="Donation frequency" data-id="element-27" data-mp="2tgswnnenktf9">
          {(['once', 'monthly'] as Frequency[]).map(f => <button key={f} type="button" role="radio" aria-checked={frequency === f} onClick={() => setFrequency(f)} className={`relative whitespace-nowrap rounded-full px-4 py-2 text-sm font-bold transition-colors duration-150 ${frequency === f ? 'text-ink' : 'text-sand/70 hover:text-sand'}`} data-id="element-28" data-mp="2eqbxkaphqflj">
              {frequency === f && <motion.span layoutId="freq-pill" className="absolute inset-0 rounded-full bg-sun" transition={{
            duration: 0.22,
            ease: EASE
          }} data-id="element-29" data-mp="20ruckkqyzh7b" />}
              <span className="relative" data-id="element-30" data-mp="1jlx1oxe5v5c">{f === 'once' ? 'One-time' : 'Monthly'}</span>
            </button>)}
        </div>
      </div>

      <AnimatePresence mode="wait" initial={false} data-id="element-31" data-mp="3or7jjp5mn8bg">
        {status === 'success' ? <motion.div key="thanks" initial={{
        opacity: 0,
        scale: 0.96
      }} animate={{
        opacity: 1,
        scale: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: EASE
      }} className="mt-8 flex flex-1 flex-col items-start justify-center gap-4" role="status" data-id="element-32" data-mp="pbax5rggfnwu">
            <CheckCircle2Icon size={40} className="text-sun" data-id="element-33" data-mp="bdcjypdvdxix" />
            <p className="font-display text-4xl uppercase leading-none" data-id="element-34" data-mp="311d89c0yhrtb">¡Gracias, familia!</p>
            <p className="text-sand/80" data-id="element-35" data-mp="2w0foww47mrq8">
              Your ${amount} {frequency === 'monthly' ? 'monthly ' : ''}gift helps keep our culture thriving in L.A.
            </p>
            <button type="button" onClick={() => setStatus('idle')} className="text-sm font-bold text-sun underline-offset-4 hover:underline" data-id="element-36" data-mp="2qw3um76hwvji">
              Give again
            </button>
          </motion.div> : <motion.div key="picker" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="mt-8 flex flex-1 flex-col" data-id="element-37" data-mp="34o1ya2hftz8a">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-5" role="radiogroup" aria-label="Donation amount" data-id="element-38" data-mp="3emr3ovs5dr7n">
              {donationAmounts.map(d => {
            const isSel = selected === d.amount;
            return <button key={d.amount} type="button" role="radio" aria-checked={isSel} onClick={() => setSelected(d.amount)} className={`rounded-2xl border-2 py-4 font-display text-2xl transition-[transform,background-color,border-color,color] duration-150 ease-out active:scale-[0.96] ${isSel ? 'border-sunset bg-sunset text-ink' : 'border-sand/20 text-sand hover:border-sand/50'}`} data-id="element-39" data-mp="1p79rkdj0mk0z">
                    ${d.amount}
                  </button>;
          })}
              <label className={`col-span-2 flex items-center rounded-2xl border-2 px-4 transition-colors duration-150 sm:col-span-1 ${selected === 'custom' ? 'border-sunset' : 'border-sand/20 hover:border-sand/50'}`} data-id="element-40" data-mp="35pxqun361jez">
                <span className="font-display text-2xl text-sand/60" data-id="element-41" data-mp="28atj6hwmby2x">$</span>
                <span className="sr-only" data-id="element-42" data-mp="288quq2b46d0b">Custom amount</span>
                <input type="number" min={1} inputMode="numeric" placeholder="Other" value={custom} onFocus={() => setSelected('custom')} onChange={e => {
              setSelected('custom');
              setCustom(e.target.value);
            }} className="w-full bg-transparent py-4 pl-1 font-display text-2xl text-sand placeholder:text-sand/40 focus:outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none" data-id="element-43" data-mp="nrpzw76o6gs2" />
              </label>
            </div>

            <div className="mt-6 min-h-[3.5rem]" data-id="element-44" data-mp="12hp16itiu8br">
              <AnimatePresence mode="wait" initial={false} data-id="element-45" data-mp="12g434j800795">
                <motion.p key={String(selected)} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.18,
              ease: EASE
            }} className="text-sand/80" data-id="element-46" data-mp="3i8a4hyjtvkay">
                  {impact}
                </motion.p>
              </AnimatePresence>
            </div>

            <button type="button" onClick={handleDonate} disabled={amount <= 0 || status === 'loading'} className="mt-auto inline-flex w-full items-center justify-center gap-2 rounded-full bg-sun px-7 py-4 font-bold text-ink transition-[transform,background-color] duration-150 ease-out hover:bg-sunset active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50" data-id="element-47" data-mp="2ubx01b3w2bfi">
              {status === 'loading' ? <Loader2Icon size={18} className="animate-spin" data-id="element-48" data-mp="26gkj9a0hbqqp" /> : <HeartIcon size={18} fill="currentColor" data-id="element-49" data-mp="9nirp5o0mbhm" />}
              {amount > 0 ? `Donate $${amount}${frequency === 'monthly' ? ' / month' : ''}` : 'Enter an amount'}
            </button>
          </motion.div>}
      </AnimatePresence>
    </div>;
}